import { useEffect } from "react";

/**
 * Prevents the Lit router's popstate listener from intercepting browser back/forward
 * navigation when the destination is outside both the Lit component's internal base
 * path and the React route that hosts the Lit component.
 *
 * The Lit acc-es-router registers a bubble-phase popstate handler that fires
 * acc-es-router-go for every navigation event. When the destination doesn't match
 * its routes, the fallback does window.location.href = pathname — a hard reload that
 * drops any hash (e.g. #group-management) from the URL.
 *
 * By registering a capture-phase listener we run before the Lit handler and, for any
 * destination outside the Lit-owned paths, stop the original event and re-dispatch a
 * synthetic PopStateEvent that the Lit router ignores but React Router can handle.
 *
 * @param litBasePath - The internal Lit router base path (e.g. "/$__los-ed/")
 * @param reactRoutePath - The React route that hosts this Lit component (e.g. "/edlos")
 *   Popstate events landing here are intentional React Router navigations and must
 *   not be intercepted.
 */
const IGNORE_STATE = "acc-es-router-ignore";

export const useLitRouterPopstateGuard = (litBasePath: string, reactRoutePath: string) => {
  useEffect(() => {
    const handlePopstate = (event: PopStateEvent) => {
      // Ignore synthetic events we dispatch ourselves so we don't recurse.
      if (event.state === IGNORE_STATE) return;

      const { pathname } = window.location;
      const isOutsideLitAndReactHosts = !pathname.startsWith(litBasePath) && pathname !== reactRoutePath;

      if (isOutsideLitAndReactHosts) {
        // Stop Lit's handler on the original event...
        event.stopImmediatePropagation();
        // ...then re-dispatch a popstate event that Lit will ignore but React Router will handle.
        const syntheticEvent = new PopStateEvent("popstate", { state: IGNORE_STATE });
        window.dispatchEvent(syntheticEvent);
      }
    };
    window.addEventListener("popstate", handlePopstate, true);
    return () => {
      window.removeEventListener("popstate", handlePopstate, true);
    };
  }, [litBasePath, reactRoutePath]);
};
